/**
 * Converts EVM address to Hedera Account ID format
 * EVM addresses are 20 bytes, we extract the last 8 bytes as the account number
 */
export function evmToHederaAccountId(evmAddress: string): string {
  // Remove '0x' prefix
  const cleanAddress = evmAddress.replace('0x', '');

  // Convert hex to number (use the full address as a big integer)
  const addressBigInt = BigInt('0x' + cleanAddress);

  // For Hedera, we use the numeric value as the account number
  const accountNumber = Number(addressBigInt);

  return `0.0.${accountNumber}`;
}

/**
 * For already zero-padded addresses (like SaucerSwap contracts), extract the actual number
 */
export function paddedEvmToHederaAccountId(evmAddress: string): string {
  // Remove '0x' prefix and extract the trailing number
  const cleanAddress = evmAddress.replace('0x', '');

  // Convert to number, removing leading zeros
  const accountNumber = parseInt(cleanAddress, 16);

  return `0.0.${accountNumber}`;
}

/**
 * Get Hedera account ID from deployment data
 */
export function getHederaAccountIdFromDeployment(evmAddress: string): string {
  // For deployment addresses, we need to calculate based on the deployment
  // These are typically sequential account IDs created during deployment

  // Convert EVM address to account number
  const cleanAddress = evmAddress.replace('0x', '');

  // Extract account number from the EVM address
  // For Hedera, the account number is embedded in the EVM address
  const accountNumber = parseInt(cleanAddress.slice(-8), 16);

  return `0.0.${accountNumber}`;
}

/**
 * Converts Hedera account ID (0.0.X format) to EVM address format for contract parameters
 * This is needed when passing Hedera addresses to contract function parameters
 */
export function hederaAccountIdToEvmAddress(hederaAccountId: string): string {
  // Parse the account number from 0.0.X format
  const parts = hederaAccountId.split('.');
  if (parts.length !== 3 || parts[0] !== '0' || parts[1] !== '0') {
    throw new Error(`Invalid Hedera account ID format: ${hederaAccountId}`);
  }

  const accountNumber = parseInt(parts[2]);
  if (isNaN(accountNumber)) {
    throw new Error(`Invalid account number in Hedera account ID: ${hederaAccountId}`);
  }

  // Convert account number to 40-character hex address
  // Pad with zeros to make it 40 characters (20 bytes)
  const hexAddress = accountNumber.toString(16).toLowerCase().padStart(40, '0');

  return '0x' + hexAddress;
}